import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfigProvider, Menu, Tooltip, Typography } from 'antd';
import type { NavigateFunction } from 'react-router-dom';
import {
  EllipsisRowTooltip,
  FilteredBuilding,
  FilteredFloor,
  FilteredSite,
  MENU_STYLE,
  MenuItem,
  RoomZoneItem,
  SIDEBAR_TREE_THEME,
  collectAllKeys,
  findAncestorChain,
  isInAllFloorsView,
  markSelected,
  maybeChildren,
  useTreeOpenKeys,
} from './shared';
import { Icon } from '../../primitives/icon/Icon';
import { MiddleTruncatedText } from '@/components/primitives/MiddleTruncatedText';
import { BuildingEditMenu } from '../BuildingEditMenu';
import { SiteEditMenu } from '../SiteEditMenu';
import { CreateDropdownButton } from '../CreateDropdownButton';
import { updateUrlParams } from '../utils';

interface MonitorTreeProps {
  sites: FilteredSite[];
  filterValue: string;
  search: string;
  navigate: NavigateFunction;
}

/**
 * Monitor's All Spaces tree.
 *
 * Single antd Menu with every level (Site / Building / Floor / Room / Zone)
 * rendered as a SubMenu or leaf in one tree. Clicking a Building / Floor /
 * Room / Zone writes the corresponding URL param and the selection lights
 * up. Sites themselves aren't selectable — clicking a Site just toggles its
 * SubMenu expand/collapse.
 *
 * Ancestor SubMenus on the path to the selection light up red automatically
 * via antd's `.ant-menu-submenu-selected` mechanism — covers Site, Building,
 * and Floor without any extra plumbing.
 */
export const MonitorTree: React.FC<MonitorTreeProps> = ({
  sites,
  filterValue,
  search,
  navigate,
}) => {
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollTopRef = useRef(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      scrollTopRef.current = el.scrollTop;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);
  // Restores the user's scroll position after antd Menu re-renders that
  // would otherwise reset scrollTop to 0 (selection / search / openKeys
  // change). No dep array because we want to re-assert scrollTop after
  // any render — but we early-return when the value already matches so
  // we don't issue redundant DOM writes (which would trigger layout) on
  // every render.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || scrollTopRef.current === 0) return;
    if (el.scrollTop === scrollTopRef.current) return;
    el.scrollTop = scrollTopRef.current;
  });

  // Selection is purely derived from the URL — no other mutator. Keeping it
  // as `useMemo` over `search` avoids the extra render the previous
  // `useState` + `useEffect` pattern introduced on every URL change.
  const selectedKey = useMemo<string | null>(() => {
    const params = new URLSearchParams(search);
    const buildingId = params.get('buildingId');
    const roomId = params.get('roomId');
    const zoneId = params.get('zoneId');
    const floorId = params.get('spaceId');
    if (buildingId) return buildingId;
    if (roomId) return roomId;
    if (zoneId) return zoneId;
    if (floorId) return floorId;
    return null;
  }, [search]);

  const items = useMemo<MenuItem[]>(() => {
    return sites.map((site) => buildSiteItem(site, { filterValue, navigate }));
  }, [sites, filterValue, navigate]);

  // Auto-expand: when selection changes, open every strict ancestor of the
  // selected item so the row is visible. The selected item itself stays
  // collapsed so its own chevron is still actionable.
  useEffect(() => {
    if (!selectedKey) return;
    const chain = findAncestorChain(items, selectedKey);
    if (!chain || chain.length === 0) return;
    const ancestorsOnly = chain.slice(0, -1);
    if (ancestorsOnly.length === 0) return;
    setOpenKeys((prev) => {
      const merged = Array.from(new Set([...prev, ...ancestorsOnly]));
      if (merged.length === prev.length) return prev;
      return merged;
    });
  }, [selectedKey, items]);

  const selectedKeys = useMemo(() => (selectedKey ? [selectedKey] : []), [selectedKey]);

  // "Show all floors": open every Site + every Building inside, leave floors
  // collapsed so the user sees a flat list of all floors at once.
  const handleShowAllFloors = useCallback(() => {
    const next: string[] = [];
    items.forEach((site) => {
      next.push(site.key);
      site.children?.forEach((building) => next.push(building.key));
    });
    setOpenKeys(next);
  }, [items]);

  // Centralized derivations: scaffolding keys + collapse-all + search-aware
  // auto-expand (which respects user-driven closes during the search).
  const { openKeysWithSearch, handleCollapseAllRooms, collapseAllRoomsVisible, recordUserClose } =
    useTreeOpenKeys({ items, filterValue, openKeys, setOpenKeys });

  // The "Show all floors" button is the inverse of the canonical all-floors
  // view — it only hides when the tree is already in that exact state.
  // We compute the effective open set (state ∪ force-included Site keys)
  // because Monitor force-injects each Site key into the Menu's `openKeys`
  // prop below; reading the raw state directly would say "site not open"
  // at mount and surface the button even though every site IS rendered.
  const effectiveOpenKeys = useMemo(() => {
    const forced = items.map((s) => String(s.key));
    return Array.from(new Set([...forced, ...openKeysWithSearch]));
  }, [items, openKeysWithSearch]);
  const showAllFloorsVisible = useMemo(
    () => !isInAllFloorsView(items, effectiveOpenKeys),
    [items, effectiveOpenKeys],
  );

  // Hoist `markSelected` out of the per-site `items.map` so we don't
  // recursively clone every node + hand antd Menu a fresh `items`
  // reference on every render.
  const markedItems = useMemo(
    () => markSelected(items, selectedKey) ?? items,
    [items, selectedKey],
  );

  return (
    <>
      <div
        // Use padding-based sizing (matches the Site SubMenu and Devices'
        // AllSpacesRow) instead of `h-9 items-center` so the All Spaces
        // label aligns vertically with the Site labels below.
        className="flex items-center justify-between my-0.5"
        style={{ padding: '6px 12px' }}
      >
        <Typography.Text strong type="secondary" className="text-xs">
          All Spaces
        </Typography.Text>
        <span style={{ display: 'inline-flex', gap: 4 }}>
          {collapseAllRoomsVisible && (
            <Tooltip title="Collapse all rooms">
              <Button
                type="text"
                size="small"
                icon={<Icon name="icon-layercollapseoutlined" size={16} />}
                onClick={handleCollapseAllRooms}
                aria-label="Collapse all rooms"
                data-id="collapse-all-rooms"
              />
            </Tooltip>
          )}
          {showAllFloorsVisible && (
            <Tooltip title="Show all floors">
              <Button
                type="text"
                size="small"
                icon={<Icon name="flooronlyoutlined" size={16} />}
                onClick={handleShowAllFloors}
                aria-label="Show all floors"
                data-id="show-all-floors"
              />
            </Tooltip>
          )}
          <CreateDropdownButton variant="icon" />
        </span>
      </div>
      <div
        ref={scrollRef}
        className="overflow-auto sidebar-scroll"
        // 200px bottom padding so the last few rows aren't flush against the
        // viewport bottom — gives the user breathing room to click / hover
        // the deepest items in the tree.
        style={{ maxHeight: 'calc(100vh - 250px)', paddingBottom: 200 }}
      >
        <ConfigProvider theme={SIDEBAR_TREE_THEME}>
          {items.map((site, siteIdx) => {
            // Each Site is its own antd Menu. Site itself is a section
            // header (non-clickable, no chevron — see `index.css`), so
            // its key is always force-included in `openKeys` and
            // Buildings inside always render. Buildings still toggle
            // individually via their own chevrons.
            const siteKeys = collectAllKeys(site);
            const siteOpen = Array.from(
              new Set([String(site.key), ...openKeysWithSearch.filter((k) => siteKeys.has(k))]),
            );
            const markedSite = markedItems[siteIdx] ?? site;
            return (
              <React.Fragment key={String(site.key)}>
                <Menu
                  data-id="sidebar-menu"
                  mode="inline"
                  inlineIndent={16}
                  items={[markedSite]}
                  selectedKeys={selectedKeys}
                  style={MENU_STYLE}
                  openKeys={siteOpen}
                  onOpenChange={(ks) => {
                    // While searching, record keys the user explicitly
                    // collapsed so the search auto-expand union doesn't
                    // immediately re-open them.
                    if (filterValue) {
                      const next = new Set(ks);
                      siteOpen.forEach((k) => {
                        if (!next.has(k)) recordUserClose(String(k));
                      });
                    }
                    // `ks` is what's open within this Site's subtree. Merge
                    // back into the global `openKeys` by replacing this
                    // site's slice (drop everything in `siteKeys`, then
                    // splice in `ks`).
                    setOpenKeys((prev) => [...prev.filter((k) => !siteKeys.has(k)), ...ks]);
                  }}
                  className="sidebar-monitor-menu"
                />
                {siteIdx < items.length - 1 && <div style={{ height: 8 }} />}
              </React.Fragment>
            );
          })}
        </ConfigProvider>
      </div>
    </>
  );
};

// ============================================================================
// Item builders — Monitor variant. Site is a non-selectable SubMenu (clicking
// only toggles expand/collapse). Building / Floor / Room / Zone all navigate
// when clicked.
// ============================================================================

function buildSiteItem(
  site: FilteredSite,
  ctx: { filterValue: string; navigate: NavigateFunction },
): MenuItem {
  return {
    key: site.id,
    label: (
      <EllipsisRowTooltip name={site.name}>
        <div className="group flex w-full items-center overflow-hidden">
          <div className="flex items-center justify-between min-w-0 flex-1 mr-1">
            {/* Site rows in Monitor are non-clickable section headers
             * (clicking only toggles expand). Render text + icon in
             * the tertiary content color so users don't read this row
             * as a navigation target. The ancestor-red CSS rule
             * `.ant-menu-submenu-selected` still wins via `!important`
             * when a child is selected. */}
            <span className="flex items-center min-w-0 overflow-hidden text-content-tertiary">
              <Icon name="icon-siteoutlined" className="mr-2 shrink-0" />
              <MiddleTruncatedText
                text={site.name}
                match={ctx.filterValue}
                className="flex-1 min-w-0"
              />
            </span>
            <span
              className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
              role="presentation"
            >
              <SiteEditMenu id={site.id} blockDelete={(site.buildings ?? []).length > 0} />
            </span>
          </div>
        </div>
      </EllipsisRowTooltip>
    ),
    ...maybeChildren(site.buildings.map((b) => buildBuildingItem(b, ctx))),
  };
}

function buildBuildingItem(
  building: FilteredBuilding,
  ctx: { filterValue: string; navigate: NavigateFunction },
): MenuItem {
  return {
    key: building.id,
    label: (
      <EllipsisRowTooltip name={building.name}>
        <div
          className="group flex w-full items-center overflow-hidden text-ellipsis hover:rounded-r-full"
          onClick={() => updateUrlParams(ctx.navigate, { buildingId: building.id })}
        >
          <div className="flex items-center justify-between min-w-0 flex-1 mr-1">
            <span className="flex items-center min-w-0 overflow-hidden">
              <Icon name="building" className="mr-2 shrink-0" />
              <MiddleTruncatedText
                text={building.name}
                match={ctx.filterValue}
                className="flex-1 min-w-0"
              />
            </span>
            <span className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              <BuildingEditMenu id={building.id} blockDelete={building.floors.length > 0} />
            </span>
          </div>
        </div>
      </EllipsisRowTooltip>
    ),
    ...maybeChildren(building.floors.map((f) => buildFloorItem(f, ctx))),
  };
}

function buildFloorItem(
  floor: FilteredFloor,
  ctx: { filterValue: string; navigate: NavigateFunction },
): MenuItem {
  return {
    key: floor.id,
    label: (
      <EllipsisRowTooltip name={floor.name}>
        <div
          className="flex items-center overflow-hidden text-ellipsis hover:rounded-r-full"
          onClick={() => updateUrlParams(ctx.navigate, { floorId: floor.id })}
        >
          <span className="flex items-center min-w-0 overflow-hidden">
            <Icon name="layer" className="mr-2" />
            <MiddleTruncatedText
              text={floor.name}
              match={ctx.filterValue}
              className="flex-1 min-w-0 mr-4"
            />
          </span>
        </div>
      </EllipsisRowTooltip>
    ),
    ...maybeChildren(floor.roomszones.map((item) => buildRoomZoneItem(floor, item, ctx))),
  };
}

function buildRoomZoneItem(
  floor: FilteredFloor,
  item: RoomZoneItem,
  ctx: { filterValue: string; navigate: NavigateFunction },
): MenuItem {
  const onClick = () =>
    item.kind === 'room'
      ? updateUrlParams(ctx.navigate, { floorId: floor.id, roomId: item.id })
      : updateUrlParams(ctx.navigate, { floorId: floor.id, zoneId: item.id });
  const result: MenuItem = {
    key: item.id,
    label: (
      <EllipsisRowTooltip name={item.name}>
        <div
          className="flex items-center overflow-hidden whitespace-nowrap text-ellipsis"
          onClick={onClick}
        >
          <span className="flex items-center min-w-0 overflow-hidden">
            <Icon name={item.kind === 'room' ? 'room' : 'zone'} className="mr-2" />
            <MiddleTruncatedText
              text={item.name}
              match={ctx.filterValue}
              className="flex-1 min-w-0 mr-4"
            />
          </span>
        </div>
      </EllipsisRowTooltip>
    ),
  };
  if (item.kind === 'room' && item.zones.length > 0) {
    result.children = item.zones.map((zone) => ({
      key: zone.id,
      label: (
        <EllipsisRowTooltip name={zone.name}>
          <div
            className="flex items-center min-w-0 overflow-hidden"
            onClick={() => updateUrlParams(ctx.navigate, { floorId: floor.id, zoneId: zone.id })}
          >
            <Icon name="zone" className="mr-2" />
            <MiddleTruncatedText
              text={zone.name}
              match={ctx.filterValue}
              className="flex-1 min-w-0 mr-4"
            />
          </div>
        </EllipsisRowTooltip>
      ),
    }));
  }
  return result;
}
